import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Loading from '../components/Loading';

// Lazy load pages/components with appropriate relative paths from src/routes/
const MapComponent = React.lazy(() => import('../components/MapComponent/MapComponent'));
const AboutSite = React.lazy(() => import('../pages/welcome/AboutSite'));
const AddTree = React.lazy(() => import('../pages/tree/AddTree'));
const AddPlantToMyGarden = React.lazy(() => import('../pages/tree/MyGarden/AddPlantToMyGarden'));
const Plant = React.lazy(() => import('../pages/tree/Plant'));
const PlantSeedling = React.lazy(() => import('../pages/tree/PlantSeedling'));
const NurseriesIndex = React.lazy(() => import('../pages/tree/Nurseries'));
const NurseryProfile = React.lazy(() => import('../pages/tree/NurseryProfile'));
const AddNursery = React.lazy(() => import('../pages/tree/AddNursery'));
const UpdateTree = React.lazy(() => import('../pages/tree/UpdateTree'));
const AddSpecie = React.lazy(() => import('../pages/tree/AddSpecie'));
const SpecieIndex = React.lazy(() => import('../pages/tree/SpeciesIndex/SpeciesIndex'));
const AddUser = React.lazy(() => import('../pages/user/AddUser'));
const TreeProfile = React.lazy(() => import('../pages/tree/TreeProfile/TreeProfile'));
const PlantProfile = React.lazy(() => import('../pages/tree/MyGarden/PlantProfile'));
const PlantPost = React.lazy(() => import('../pages/tree/MyGarden/PlantPost'));
const PlantToGarden = React.lazy(() => import('../pages/tree/MyGarden/PlantToGarden'));
const TreeVote = React.lazy(() => import('../pages/tree/TreeVote'));
const TreePost = React.lazy(() => import('../pages/tree/TreePost'));
const UserProfile = React.lazy(() => import('../pages/user/UserProfile'));
const UserByUsername = React.lazy(() => import('../components/UserByUserName'));
const AdminFrontPage = React.lazy(() => import('../pages/admin/AdminFrontPageTabs'));
const AdminDashboard = React.lazy(() => import('../pages/admin/AdminDashboard'));
const AdminPrefeituras = React.lazy(() => import('../pages/admin/AdminPrefeituras'));
const SearchEditPage = React.lazy(() => import('../pages/admin/SearchEditPage'));
const SatelliteSurvey = React.lazy(() => import('../pages/admin/satellite/SatelliteSurvey'));
const SatelliteSurveyStreet = React.lazy(() => import('../pages/admin/satellite/SatelliteSurveyStreet'));
const AdminTreeSummary = React.lazy(() => import('../pages/admin/AdminTreeSummary'));
const CentralNotifications = React.lazy(() => import('../pages/notifications/CentralNotifications'));
const Welcome = React.lazy(() => import('../pages/welcome/Welcome'));
const Feed = React.lazy(() => import('../pages/feed/YourFeed'));
const MapWithShapes = React.lazy(() => import('../components/MapWithShapes'));
const AddSeedling = React.lazy(() => import('../pages/tree/AddSeedling'));
const TreeDuplicate = React.lazy(() => import('../pages/tree/TreeDuplicate'));
const TreeNewLocation = React.lazy(() => import('../pages/tree/TreeNewLocation'));
const BlogEditor = React.lazy(() => import('../pages/blog/BlogEditor'));
const BlogArticle = React.lazy(() => import('../pages/blog/BlogArticle'));
const BlogMapTrees = React.lazy(() => import('../pages/blog/BlogMapTrees'));
const Privacy = React.lazy(() => import('../pages/welcome/Privacy'));
const ContactPage = React.lazy(() => import('../pages/welcome/ContactUs'));
const Community = React.lazy(() => import('../pages/community/Community'));
const IdentifyTreesPage = React.lazy(() => import('../pages/tree/IdentifyTreesPage'));
const AdminTreeSizePage = React.lazy(() => import('../pages/tree/AdminTreeSizePage'));
const ProCityDashboard = React.lazy(() => import('../pages/user/pro/ProCityDashboard'));
const ProManagement = React.lazy(() => import('../pages/user/pro/ProManagement'));
const ProConfidentialFields = React.lazy(() => import('../pages/user/pro/ProConfidentialFields'));
const ProSeedlingsManagement = React.lazy(() => import('../pages/user/pro/ProSeedlingsManagement'));
const UnifespMudaScanner = React.lazy(() => import('../pages/user/pro/UnifespMudaScanner'));


export default function AppRoutes({ authProps }) {
  const { user, isLoading, acceptUserTerms } = authProps;

  return (
    <React.Suspense fallback={<Loading />}>
      <Routes>
        <Route
          path="/"
          element={
            isLoading ? (
              <div><Loading /></div>
            ) : user ? (
              <MapComponent />
            ) : (
              <Navigate to="/welcome" replace />
            )
          }
        />
        <Route path="/map-with-shapes" element={<MapWithShapes />} />
        <Route path="/feed" element={<Feed />} />

        <Route
          path="/welcome"
          element={
            isLoading ? (
              <Loading />
            ) : user && acceptUserTerms ? (
              <Navigate to="/" replace />
            ) : (
              <Welcome authProps={authProps} />
            )
          }
        />

        <Route path="/privacy-policy/" element={<Privacy />} />
        <Route path="/contact-us/" element={<ContactPage />} />
        <Route path="/about-us/" element={<AboutSite />} />
        <Route path="/centralnotifications" element={<CentralNotifications />} />
        <Route path="/community" element={<Community />} />

        {/* Blog Routes */}
        <Route path="/write-article/:treeId" element={<BlogEditor />} /> 
        <Route path="/edit-article/:articleId" element={<BlogEditor />} />
        <Route path="/edit-planting/:plantingId" element={<BlogEditor Plantio={true} />} />
        <Route path="/write-article" element={<BlogEditor />} />
        <Route path="/article/:articleId" element={<BlogArticle />} />
        <Route path="/planting/:plantingId" element={<BlogArticle />} />
        <Route path="/planting/" element={<BlogEditor Plantio={true} />} />
        <Route path="/add-tree/:plantingId?" element={<AddTree />} />
        <Route path="/blog" element={<BlogMapTrees />} />

        {/* Species Routes */}
        <Route path="/addspecie" element={<AddSpecie />} />
        <Route path="/addspecie/:id" element={<AddSpecie />} />
        <Route path="/specie-index" element={<SpecieIndex />} />

        {/* Trees & Plants Profiles */}
        <Route path="/tree-profile/:id" element={<TreeProfile />} />
        <Route path="/plant-profile/:plantId" element={<PlantProfile />} />

        <Route path="/tree-vote/:id" element={<TreeVote />} />
        <Route path="/tree-post/:id" element={<TreePost />} />
        <Route path="/plant-post/:plantId" element={<PlantPost />} />
        <Route path="/tree-post/:id/:plantingId?" element={<TreePost />} />
        
        {/* Garden Routes */}
        <Route path="/add-plant/:albumId" element={<AddPlantToMyGarden />} />
        <Route path="/plant-to-garden/:plantId" element={<PlantToGarden />} />

        <Route path="/addtree" element={<AddTree />} />
        <Route path="/tree-duplicate/:id" element={<TreeDuplicate />} />
        <Route path="/tree-new-location/:id" element={<TreeNewLocation />} />
        <Route path="/plant/:id" element={<Plant />} />
        <Route path="/updatetree" element={<UpdateTree />} />
        <Route path="/plantseedling" element={<PlantSeedling />} />
        <Route path="/nurseries" element={<NurseriesIndex />} />
        <Route path="/nursery-profile/:id" element={<NurseryProfile />} />
        <Route path="/nursery/:id" element={<NurseryProfile />} />
        <Route path="/addnursery" element={<AddNursery />} />
        <Route path="/addseedling" element={<AddSeedling />} />
        <Route path="/identifytrees" element={<IdentifyTreesPage />} />

        {/* Admin Routes */}
        <Route path="/admin-front-page" element={<AdminFrontPage defaultTab="users" />} />
        <Route path="/admin/dashboard" element={<AdminFrontPage defaultTab="dashboard" />} />
        <Route path="/admin/prefeituras" element={<AdminFrontPage defaultTab="prefeituras" />} />
        <Route path="/admin/satellite" element={<AdminFrontPage defaultTab="satellite" />} />
        <Route path="/admin/satellite-street" element={<AdminFrontPage defaultTab="satellite-street" />} />
        <Route path="/search-edit-page" element={<SearchEditPage />} />
        <Route path="/admin/tree-summary/:id" element={<AdminTreeSummary />} />
        <Route path="/admin-porte" element={<AdminTreeSizePage authProps={authProps} />} />
        
        {/* User Routes */}
        <Route path="/addUser" element={<AddUser />} />
        <Route path="/userprofile/:id" element={<UserProfile />} />
        <Route path="/:username" element={<UserByUsername />} />
        <Route path="/user-profile-other/:id" element={<UserProfile />} />

        {/* Pro Routes */}
        <Route path="/pro/city-dashboard" element={<ProCityDashboard authProps={authProps} />} />
        <Route path="/pro/management" element={<ProManagement authProps={authProps} />} />
        <Route path="/pro/confidential-fields" element={<ProConfidentialFields authProps={authProps} />} />
        <Route path="/pro/seedlings" element={<ProSeedlingsManagement authProps={authProps} />} />
        <Route path="/unifesp/localizar-muda" element={<UnifespMudaScanner authProps={authProps} />} />
        <Route path="/pro/localizar-muda" element={<UnifespMudaScanner authProps={authProps} />} />
      </Routes>
    </React.Suspense>
  );
}
